import { React, memo, useCallback, useState, useEffect } from 'react'
import Select from 'react-select'
import MapIcon from "../../../../../assets/icons/google-maps-icon.png"
import { constrainToSegmentBounds, findClosestSegment, projectPointOnLine } from "../../../../../utils/locationUtils"
import { getUSandDSAgms, getUSandDSDists } from "../../../../../utils/poi/poiHookHelpers";
import { formatDecimalPlaces } from "../../../../../utils/formatDecimalPlaces";

// ********* helper functions that don't depend on state **********/
const getSiteName = (agm) => {
    if(!agm) return ""
    return agm.Site_Name
}

/**
 * Memoized row for the Poi Table component
 * 
 * @param {Object} poi - data for one poi
 * @param {number} poiIndex 
 * @param {Object} handlers - reusable handler functions from PoiTable parent
 * @param {DropdownOption} poiGroupOptions 
 * @param {Array} polyLineArr - array of AGMs and Subchains (from useLiveSiteMap) for lat/lng calculations
 * @param {Object} errors - validation and submit errors, so cells or row can be outlined
 * @param {Object} userPermissions - contains booleans describing if user is admin or POI creator
 * @param {number | null} fetchedByGroupId - render items different if POIs were fetched by group
 * 
 * @todo maybe we want to give a warning to the user before submitting if they are making changes to lat/lng and there are linkedPOIs?
 */
export const PoiTableRow = memo(({
    poi, 
    poiIndex, 
    handlers,
    poiGroupOptions, 
    polyLineArr,
    errors,
    userPermissions,
    fetchedByGroupId,
}) => {
    // ********* destructure props (to keep props looking clean) **********/
    const {
        handlePoiChange,
        handleGroupChange,
        addPoi,
        handleDeleteOnePOI,
        handleOpenPoiOnMap
    } = handlers;

    const {
        validationErrors,
        submitError
    } = errors

    const {
        userIsAdmin,
    } = userPermissions


    // ********* STATE **********/

    //editable
    const [name, setName] = useState()
    const [description, setDescription] = useState() 

    const [lat, setLat] = useState(poi.Click_Point_Lat)
    const [lng, setLng] = useState(poi.Click_Point_Lng)

    //calculated, not editable by user
    const [intersectionLat, setIntersectionLat] = useState(poi.Intersect_Point_Lat)
    const [intersectionLng, setIntersectionLng] = useState(poi.Intersect_Point_Lng)

    const [distUS, setDistUS] = useState(poi.Dist_To_US)
    const [distDS, setDistDS] = useState(poi.Dist_To_DS)

    const [usSite, setUsSite] = useState(getSiteName(polyLineArr.find(item => item.AGMID && item.AGMID === poi.US_Site)))
    const [dsSite, setDsSite] = useState(getSiteName(polyLineArr.find(item => item.AGMID && item.AGMID === poi.DS_Site)))

    //name and description could change depending on if poi is fetched by group or 'all pois for job', so update them if poi changes
    useEffect(() => {
        setDescription(fetchedByGroupId && poi.AltDescription ? poi.AltDescription : poi.Description); //if fetched by group use alt description, otherwise just use original POI description
        setName(fetchedByGroupId && poi.AltPOI_Name ? poi.AltPOI_Name : poi.POI_Name)
    }, [poi, fetchedByGroupId]);

    //validation helper
    const isCellInvalid = useCallback((columnKey) => {
        return validationErrors.some(error => error.column === columnKey);
    }, [validationErrors])

    //styling
    const inputClasses = "px-2 py-1 border border-gray-300 rounded max-w-[120px] sm:max-w-[200px]"
    

    // ********* HANDLER FUNCTIONS **********/

    //when lat or lng changes, recalculate intersection point, distance to us/ds, us/ds site
    const recalculatePOIData = () => {
        
        //user poi utils to make calculations
        const newPosition = {
            lat,
            lng
        }
        const closestSegment = findClosestSegment(newPosition, polyLineArr);

        const constrainedPosition = projectPointOnLine(
            newPosition,
            closestSegment.closestSegment.upstream,
            closestSegment.closestSegment.downstream
        );

        const finalPosition = constrainToSegmentBounds(
            constrainedPosition,
            closestSegment.closestSegment.upstream,
            closestSegment.closestSegment.downstream
        );


        const {upstreamAgm, downstreamAgm} = getUSandDSAgms(closestSegment, polyLineArr);

        const { distToUS, distToDS } = getUSandDSDists(upstreamAgm, downstreamAgm, finalPosition);

        //set state for intersection, distance, and us/ds site fields
        setIntersectionLat(finalPosition.lat)
        setIntersectionLng(finalPosition.lng)

        setDistUS(distToUS)
        setDistDS(distToDS)

        setUsSite(upstreamAgm.Site_Name)
        setDsSite(downstreamAgm.Site_Name)

        //update parent component
        handlePoiChange(
            {
                'Intersect_Point_Lat': finalPosition.lat,
                'Intersect_Point_Lng': finalPosition.lng,
                'Dist_To_US': distToUS,
                'Dist_To_DS': distToDS,
                'US_Site': upstreamAgm.AGMID,
                'DS_Site': downstreamAgm.AGMID

            },
            poiIndex
        )

    }

    const handleCoordinateChange = (coordinate) => {
        //check if both lat and lng exist
        //if so, recalculate intersection point and distances to us/ds
        if(lat && lng){
            recalculatePOIData()
        }

        //update parent component state
        if(coordinate === "lat"){
            handlePoiChange({'Click_Point_Lat': Number(lat)}, poiIndex)
        }
        if(coordinate === "lng"){
            handlePoiChange({'Click_Point_Lng': Number(lng)}, poiIndex)
        }

    }

    const handleDescriptionChange = useCallback(() => {
        //if it's a linked poi - the poi was fetched by group - update the description of the linked poi
        //also check if it's a new poi (has no POIID) - if it's new, there are no linkedPOIs to update
        if(fetchedByGroupId && poi.POIID){

            //make deep copy
            const linkedPOIsCopy = [...poi.linkedPOIs.map(linkedPOI => ({...linkedPOI}))]
            
            //replace linkedPOIs
            const newLinkedPOIs = linkedPOIsCopy.map(linkedPOI => {
                if(linkedPOI.LPOIID === poi.LPOIID){
                    return {
                        ...linkedPOI,
                        Description: description
                    }
                }
                return linkedPOI
            })

            //update the linkedPOIs object in parent
            handlePoiChange({
                'linkedPOIs': newLinkedPOIs,
                'AltDescription': description
            }, poiIndex)

        } else {
            //otherwise, update the poi's original description
            handlePoiChange({'Description': description}, poiIndex)
        }
    }, [poi, handlePoiChange, description, fetchedByGroupId, poiIndex])

    const handleNameChange = useCallback(() => {
        //if it's a linked poi - the poi was fetched by group - update the name of the linked poi
        //also check if it's a new poi (has no POIID) - if it's new, there are no linkedPOIs to update
        if(fetchedByGroupId && poi.POIID){

            //make deep copy
            const linkedPOIsCopy = [...poi.linkedPOIs.map(linkedPOI => ({...linkedPOI}))]
            
            //replace linkedPOIs
            const newLinkedPOIs = linkedPOIsCopy.map(linkedPOI => {
                if(linkedPOI.LPOIID === poi.LPOIID){
                    return {
                        ...linkedPOI,
                        POI_Name: name
                    }
                }
                return linkedPOI
            })

            //update the linkedPOIs object in parent
            handlePoiChange({
                'linkedPOIs': newLinkedPOIs,
                'AltPOI_Name': name
            }, poiIndex)

        } else {
            //otherwise, update the poi's original description
            handlePoiChange({'POI_Name': name}, poiIndex)
        }
    }, [poi, handlePoiChange, name, fetchedByGroupId, poiIndex])


    // ********* MAIN COMPONENT **********/

    return (
        <>
            <tr className={` ${submitError ? 'border border-red-400' : ' border-t border-gray-200'}`}>

                {/* poi name */}
                <td className="px-4 py-2 sticky-left bg-white">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={handleNameChange}
                        className={isCellInvalid('POI_Name') ? `${inputClasses} martrak-error-border` : `${inputClasses} `}
                        disabled={!userIsAdmin}
                    />
                </td>

                {/* original name */}
                {fetchedByGroupId && (
                    <td className="px-4 py-2">
                        {poi.AltPOI_Name ? poi.POI_Name : null}
                    </td>
                )}

                {/* site type - POI or AGM */}
                <td className="px-4 py-2">
                    POI
                </td>
                
                {/* description */}
                <td className="px-4 py-2">
                    <textarea
                        value={description}
                        onChange={(e) => {setDescription(e.target.value)}}
                        onBlur={handleDescriptionChange}
                        className={inputClasses}
                        disabled={!userIsAdmin}
                    />
                </td>

                {/* group selection */}
                <td className="px-4 py-2">
                    <div className="select-control w-[150px] sm:w-[250px]">
                        <Select
                            options={poiGroupOptions}
                            value={poi.POIGroups}
                            onChange={(e) => handleGroupChange(e, poiIndex)}
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                            isMulti
                            isDisabled={!userIsAdmin}
                        />
                                
                    </div>
                </td>

                {/* clicked point lat */}
                <td className="px-4 py-2">
                    <input
                        type="number"
                        value={formatDecimalPlaces(lat, 5)}
                        onChange={(e) => {setLat(Number(e.target.value))}}
                        onBlur={() => handleCoordinateChange("lat")}
                        className={isCellInvalid('Click_Point_Lat') ? `${inputClasses} martrak-error-border` : inputClasses}
                        disabled={!userIsAdmin}
                    />
                </td>
                            
                {/* clicked point lng */}
                <td className="px-4 py-2">
                    <input
                        type="number"
                        value={formatDecimalPlaces(lng, 5)}
                        onChange={(e) => setLng(Number(e.target.value))}
                        onBlur={() => handleCoordinateChange("lng")}
                        className={isCellInvalid('Click_Point_Lng') ? `${inputClasses} martrak-error-border` : inputClasses}
                        disabled={!userIsAdmin}
                    />
                </td>

                {/* intersection point lat */}
                <td className="px-4 py-2">
                    <input
                        type="number"
                        value={formatDecimalPlaces(intersectionLat, 5)}
                        onChange={(e) => handlePoiChange({'Intersect_Point_Lat': e.target.value}, poiIndex)}
                        className={inputClasses}
                        disabled
                    />
                </td>

                {/* intersection point lng */}
                <td className="px-4 py-2">
                    <input
                        type="number"
                        value={formatDecimalPlaces(intersectionLng, 5)}
                        onChange={(e) => handlePoiChange({'Intersect_Point_Lng': e.target.value}, poiIndex)}
                        className={inputClasses}
                        disabled
                    />
                </td>

                {/* upstream site*/}
                <td className="px-4 py-2 text-center">
                    {usSite}
                </td>

                {/* distance to upstream */}
                <td className="px-4 py-2">
                    <input
                        type="number"
                        value={formatDecimalPlaces(distUS, 3)}
                        onChange={(e) => handlePoiChange({'Dist_To_US':e.target.value}, poiIndex)}
                        className={inputClasses}
                        disabled
                    />
                </td>

                   
                {/* downstream site */}
                <td className="px-4 py-2 text-center">
                    {dsSite}
                </td>

                {/* distance to downstream */}      
                <td className="px-4 py-2">
                    <input
                        type="number"
                        value={formatDecimalPlaces(distDS, 3)}
                        onChange={(e) => handlePoiChange({'Dist_To_DS': e.target.value}, poiIndex)}
                        className={inputClasses}
                        disabled
                    />
                </td>
                
                {userIsAdmin ? (
                    <td  className="text-center p-2">
                    <button
                        onClick={() => addPoi(poiIndex + 1)}
                        className="text-blue-500 hover:text-blue-700 w-max"
                    >
                        <i className="fa-solid fa-down-long"></i> Add
                    </button>
                </td>
                ) : null}
                {/* add new poi below this row */}
                

                {/* view poi on map - for already saved pois */}
                {poi.POIID ? (
                    <td>
                    <div className='px-4 py-2 flex justify-center items-center'>
                        <img 
                            src={MapIcon} 
                            alt="google map icon - view POI on map" 
                            className='w-6 hover:cursor-pointer'
                            onClick={() => handleOpenPoiOnMap(poi.POIID, fetchedByGroupId ?? null)}
                        />
                    </div>
                    
                </td>
                ) : (
                <td></td>
                )}
                

                {/* delete poi */}
                {userIsAdmin ? (
                    <td >
                        <div className='px-4 py-2 flex justify-center items-center'>
                            <i
                                className="fa-regular fa-trash-can"
                                onClick={() => handleDeleteOnePOI(poi, poiIndex)}
                                style={{ color: "red", fontSize: "20px" }}
                            />    
                        </div>
                    
                    </td>
                ) : null}
                
            </tr>

        </>
    )
  })